import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Monthly Concierge reports for one athlete, newest first. Shown to staff and
 * to the athlete's owning partner; the server scopes which athletes and months
 * each may see. Clicking a month opens its PDF via a 5-minute presigned URL.
 *
 * The blank window is opened synchronously inside the click handler: a
 * window.open after the await would no longer count as a user gesture and
 * popup blockers would eat it. generated_at is naive UTC and is deliberately
 * not shown; the month label is all that matters.
 */
export default function ConciergeReportsCard({ userId, API_URL, cfg }) {
  const [reports, setReports] = useState(null); // null = loading
  const [loadErr, setLoadErr] = useState(false);
  const [openErr, setOpenErr] = useState(false);
  // The page rebuilds cfg on every render; read it through a ref so the list
  // reloads only when the athlete changes.
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;

  useEffect(() => {
    let live = true;
    setReports(null);
    setLoadErr(false);
    setOpenErr(false);
    axios.get(`${API_URL}/concierge/${userId}/reports`, cfgRef.current)
      .then((res) => { if (live) setReports(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (live) setLoadErr(true); });
    return () => { live = false; };
  }, [userId, API_URL]);

  const openReport = (id) => {
    setOpenErr(false);
    const w = window.open('', '_blank');
    axios.get(`${API_URL}/concierge/${userId}/reports/${id}/url`, cfg)
      .then((res) => {
        const url = res && res.data && res.data.url;
        if (!url) throw new Error('no url');
        if (w) {
          w.opener = null; // the S3 page must not reach back into this tab
          w.location.href = url;
        }
      })
      .catch(() => {
        if (w) w.close();
        setOpenErr(true);
      });
  };

  return (
    <section className="cn-card cn-reports__card" aria-labelledby="cn-reports-title">
      <div className="cn-card__head">
        <h3 className="cn-card__title" id="cn-reports-title">Reports</h3>
      </div>
      {loadErr && <div className="cn-empty-line cn-empty-line--err">Couldn't load reports.</div>}
      {!loadErr && reports && reports.length === 0 && (
        <div className="cn-empty-line">No reports yet — the first is made on the last day of the month.</div>
      )}
      {!loadErr && reports && reports.length > 0 && (
        <ul className="cn-reports__list">
          {reports.map((r) => (
            <li key={r.id}>
              <button type="button" className="cn-reports__link" onClick={() => openReport(r.id)}>
                {r.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {openErr && <div className="cn-reports__err cn-inline-msg cn-inline-msg--err">Couldn't open the report.</div>}
    </section>
  );
}
